import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Grid,
    Typography,
    Chip,
    IconButton,
    Tooltip,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper
} from '@mui/material';
import { Close as CloseIcon, Science as ScienceIcon, Info as InfoIcon } from '@mui/icons-material';

const RunDetail = ({ run, onClose }) => {
    if (!run) return null;

    const {
        runName,
        runDate,
        runDateTime,
        instrument,
        testResults = [],
        analytes = [],
        qcSamples = [],
        resultCount
    } = run;

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        const d = new Date(dateStr);
        return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString();
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return 'N/A';
        const d = new Date(dateStr);
        return isNaN(d.getTime()) ? dateStr : d.toLocaleString();
    };

    const getStatusColor = (value) => {
        if (!value) return 'default';
        return value === 'Yes' ? 'success' : 'error';
    };

    const buildTooltipContent = (tr) => {
        const lines = [];
        if (tr.resultName) lines.push(`Result: ${tr.resultName}`);
        if (tr.qcSetNumber) lines.push(`QC Set #: ${tr.qcSetNumber}`);
        if (tr.batchName) lines.push(`Batch: ${tr.batchName}`);
        if (tr.instrumentRange) lines.push(`Instrument Range: ${tr.instrumentRange}`);
        if (tr.instrumentResultStatus) lines.push(`Instrument Status: ${tr.instrumentResultStatus}`);
        if (tr.instrumentFlags) lines.push(`Instrument Flags: ${tr.instrumentFlags}`);
        if (tr.instrumentComments) lines.push(`Instrument Comments: ${tr.instrumentComments}`);
        if (tr.qcGrouping) lines.push(`QC Grouping: ${tr.qcGrouping}`);
        if (tr.comments) lines.push(`Comments: ${tr.comments}`);
        return lines.length > 0 ? lines : ['No additional details'];
    };

    const renderField = (label, value) => (
        <Grid item xs={12} sm={6} md={4}>
            <Typography variant="caption" color="text.secondary" display="block">
                {label}
            </Typography>
            <Typography variant="body1">
                {value || 'N/A'}
            </Typography>
        </Grid>
    );

    return (
        <Dialog
            open={true}
            onClose={onClose}
            fullWidth
            maxWidth="xl"
            PaperProps={{ sx: { minHeight: '60vh' } }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ScienceIcon color="primary" />
                    <Typography variant="h6" component="span">
                        {runName}
                    </Typography>
                    <Chip label={instrument || 'No Instrument'} size="small" variant="outlined" />
                    <Chip label={`${resultCount || testResults.length} results`} size="small" color="primary" />
                </Box>
                <IconButton onClick={onClose} size="small" aria-label="close">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {/* Run Info */}
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                    Run Information
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Grid container spacing={2}>
                        {renderField('Run Name', runName)}
                        {renderField('Run Date', formatDate(runDate))}
                        {renderField('Run Date/Time', formatDateTime(runDateTime))}
                        {renderField('Instrument', instrument)}
                        {renderField('Total Results', String(resultCount || testResults.length))}
                    </Grid>
                </Paper>

                {/* Analytes */}
                {analytes.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            Analytes ({analytes.length})
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {analytes.map((a, idx) => (
                                <Chip key={idx} label={a} size="small" color="primary" variant="outlined" />
                            ))}
                        </Box>
                    </Box>
                )}

                {/* QC Samples */}
                {qcSamples.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            QC Samples ({qcSamples.length})
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {qcSamples.map((s, idx) => (
                                <Chip key={idx} label={s} size="small" color="secondary" variant="outlined" />
                            ))}
                        </Box>
                    </Box>
                )}

                {/* Test Results Table */}
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                    Test Results ({testResults.length})
                </Typography>

                {testResults.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No test results found for this run.
                    </Typography>
                ) : (
                    <Paper variant="outlined" sx={{ overflow: 'auto' }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600, width: 30 }}></TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Analyte</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>QC Sample</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>QC Range</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="right">Value</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="center">Within 10%</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="center">Within 20%</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="center">Group Valid</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Date/Time</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Comments</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {testResults.map((tr, idx) => (
                                    <TableRow
                                        key={tr.legacyResultId || idx}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                        <TableCell sx={{ p: 0.5 }}>
                                            <Tooltip
                                                arrow
                                                placement="right"
                                                title={
                                                    <Box sx={{ fontSize: '0.8rem', lineHeight: 1.6 }}>
                                                        {buildTooltipContent(tr).map((line, i) => (
                                                            <div key={i}>{line}</div>
                                                        ))}
                                                    </Box>
                                                }
                                            >
                                                <InfoIcon sx={{ fontSize: 16, color: 'action.active', cursor: 'pointer' }} />
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>{tr.analyte || '-'}</TableCell>
                                        <TableCell>{tr.qcSample || '-'}</TableCell>
                                        <TableCell>{tr.qcRange || '-'}</TableCell>
                                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                                            {tr.value || '-'}
                                        </TableCell>
                                        <TableCell align="center">
                                            {tr.within10Pct && (
                                                <Chip
                                                    label={tr.within10Pct}
                                                    size="small"
                                                    color={getStatusColor(tr.within10Pct)}
                                                    variant="outlined"
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            {tr.within20Pct && (
                                                <Chip
                                                    label={tr.within20Pct}
                                                    size="small"
                                                    color={getStatusColor(tr.within20Pct)}
                                                    variant="outlined"
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            {tr.qcGroupValid && (
                                                <Chip
                                                    label={tr.qcGroupValid}
                                                    size="small"
                                                    color={getStatusColor(tr.qcGroupValid)}
                                                    variant="outlined"
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {formatDateTime(tr.dateTimeCompleted)}
                                        </TableCell>
                                        <TableCell>
                                            {tr.comments ? (
                                                <Tooltip arrow title={tr.comments}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}
                                                    >
                                                        {tr.comments}
                                                    </Typography>
                                                </Tooltip>
                                            ) : ''}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default RunDetail;
